import React, { FC, ReactElement, ReactNode, } from "react";
import {
    RerollCard,
    RollType,
    PoolRollResult,
    SingleRollResult,
    PoolRollOutcome,
    getPoolOutcome,
} from ".";
import { classNames, } from "components/Utils";

type Props = {
    rollResult?: PoolRollResult | SingleRollResult;
    onReroll: (pool: number[]) => void;
};

export const Results: FC<Props> = (props: Props): ReactElement => {
    const { rollResult, onReroll, } = props;

    // no results, nothing to do
    if (!rollResult) {
        return <></>;
    }

    const isPool = rollResult.type === RollType.Pool;

    const content =  isPool ?
        renderPoolResult(rollResult as PoolRollResult) :
        renderSingleRollResult(rollResult as SingleRollResult);

    const rerollContent = isPool && (!(rollResult as PoolRollResult).reroll) ?
        <RerollCard result={rollResult as PoolRollResult} onReroll={onReroll} />:
        undefined;

    const containerCss = classNames([
        "__results__",
    ]);
    const contentCss = classNames([
        rerollContent ? "mb-3" : "mb-0",
    ]);

    return (
        <div className={containerCss}>
            <div className={contentCss}>
                {content}
            </div>

            {rerollContent}
        </div>
    );
};

const renderTimestamp = (timestamp: number): ReactElement => {
    const d = new Date(timestamp);

    const h = d.getHours();
    const m = d.getMinutes();
    const s = d.getSeconds();

    const hh = `${h}`.padStart(2, "0");
    const mm = `${m}`.padStart(2, "0");
    const ss = `${s}`.padStart(2, "0");

    return (
        <div className="row">
            <div className="__timestamp__">
                <span className="hours">{hh}</span>
                <span className="separator">:</span>
                <span className="minutes">{mm}</span>
                <span className="separator">:</span>
                <span className="seconds">{ss}</span>
            </div>
        </div>
    );
};

const renderSingleRollResult = (result: SingleRollResult): ReactElement => {
    const { type, } = result;
    if (type === RollType.D10) {
        return renderD10Result(result);
    }

    return renderRouseResult(result);
};

const renderRouseResult = (result: SingleRollResult): ReactElement => {
    const { roll, timestamp, } = result;

    // output elements
    const failure = (
        <>
            <strong className="text-danger">Failure.</strong>
            <span> Gain one point of <strong>Hunger</strong>.</span>
        </>
    );

    const success = (
        <>
            <strong className="text-success">Success.</strong>
            <span> No <strong>Hunger</strong> points have been gained.</span>
        </>
    );

    const outcome = (roll < 6) ? failure : success;

    const time = renderTimestamp(timestamp);

    return (
        <>
            {time}
            <div className="row">
                <h3>Rouse</h3>

                <p className="mb-0">You rolled <strong>{roll}</strong>. {outcome}</p>
            </div>
        </>
    );
};

const renderD10Result = (result: SingleRollResult): ReactElement => {
    const { roll, timestamp, } = result;

    const time = renderTimestamp(timestamp);

    // output elements
    return (
        <>
            {time}
            <div className="row">

                <h3>D10</h3>

                <p className="mb-0">You rolled <strong>{roll}</strong>.</p>
            </div>
        </>
    );
};

const renderPoolResult = (result: PoolRollResult): ReactElement => {
    const { difficulty, timestamp, } = result;

    const time = renderTimestamp(timestamp);
    const rolls = renderPoolDiceRolls(result);
    const outcome = getPoolOutcome(result);

    return (
        <>
            {time}

            {rolls}

            {!difficulty && renderPoolResultText(outcome)}

            {difficulty && renderPoolResultCard(outcome)}
        </>
    );
};

const renderPoolDiceRolls = (result: PoolRollResult): ReactElement => {
    const { pool, hunger, } = result;

    const poolRolls = renderPoolDiceRollContent(pool);
    const hungerRolls = renderPoolDiceRollContent(hunger, true);

    return (
        <div className="row mb-3">
            <div className="col-6">
                <h3>Pool</h3>
                {poolRolls}
            </div>

            <div className="col-6">
                <h3>Hunger</h3>
                {hungerRolls}
            </div>
        </div>
    );
};

const renderPoolDiceRollContent = (rolls: number[] | undefined, isHunger?: boolean, listOnly?: boolean): ReactNode => {
    // no dice rolled
    if (!rolls || !rolls.length) {
        return (<span>&mdash;</span>);
    }

    // wrap the results in spans
    const tags = rolls.map<ReactNode>((num, index) => {
        const css = num === 10 ? "text-success" : (num === 1 && isHunger) ? "text-danger" : undefined;
        return (
            <strong key={index} className={css}>{num}</strong>
        );
    });

    // build the natural-language list
    const list = tags.reduce((prev, curr, index) => {
        if (tags.length === 2) {
            return [prev, " and ", curr];
        }

        if (index === tags.length - 1) {
            return [prev, ", and ", curr];
        }

        return [prev, ", ", curr];
    });

    if (listOnly) {
        return list;
    }

    return (
        <span>You rolled {list}.</span>
    );
};

export const renderPoolResultText = (outcome: PoolRollOutcome): ReactNode => {
    const { successes, poolCrits, hungerCrits, hungerFailures, } = outcome;

    // generate the success text; we always render this
    const successText = (
        <span key={"successes"}>
            <strong>{`${successes}`}</strong> {`${successes === 1 ? "success" : "successes"}`}.
        </span>
    );

    // create a store for the content we'll output
    const content = [successText] as (ReactElement | string)[];
    const addContent = (element: ReactElement) => {
        // add a space between elements
        content.push(" ");
        content.push(element);
    };


    if (poolCrits + (hungerCrits || 0) > 1) {
        // we have crits...
        const poolCrit = <strong className="text-success" key={"crit"}>Possible critical success.</strong>;
        const hungerCrit = <strong className="text-messy" key={"messy"}>Possible messy critical.</strong>;

        if (!hungerCrits) {
            // ... but no hunger crits
            // possibleCrit = poolCrit;
            // content.push(poolCrit);
            addContent(poolCrit);
        } else {
            // we have hunger crits
            if ((hungerCrits > 1) || (poolCrits % 2 !== 0)) {
                // at least a pair of crits: 2+ hunger crits; or 1+ hunger crit and 1+ pool crit
                addContent(hungerCrit);
            } else {
                addContent(poolCrit);
            }
        }
    }

    if (hungerFailures) {
        // bestial failure
        const possibleFailure = <strong className="text-danger" key={"bestial"}>Possible bestial failure.</strong>;
        addContent(possibleFailure);
    }

    return (
        <div className="row">
            <h3>Result</h3>
            <p className="mb-0">{content}</p>
        </div>
    );
};

export const renderPoolResultCard = (outcome: PoolRollOutcome): ReactNode => {
    const { successes, difficulty, hungerCrits, poolCrits, hungerFailures, } = outcome;

    let className = "";
    let elements: ReactElement[] = [];
    const successContent = <span><strong>{`${successes}`}</strong> {`${successes === 1 ? "success" : "successes"}`},</span>;
    let marginContent: ReactElement;

    // did we succeed?
    if (successes >= (difficulty || 0)) {
        // success!
        if (((hungerCrits || 0) > 1) || (hungerCrits && ((poolCrits + hungerCrits) % 2 === 0))) {
            elements.push(<h1 key={4}>Messy critical</h1>);
            className = "messy";
        } else {
            if (poolCrits > 1) {
                // critical
                elements.push(<h1 key={4}>Critical success</h1>);
                className = "success";
            } else {
                // regular
                elements.push(<h1 key={4}>Success</h1>);
                className = "win";
            }
        }

        marginContent = <span>a <strong>{successes - (difficulty || 0)}</strong>-degree margin of success.</span>;
    } else {
        // failure
        if (hungerFailures) {
            // bestial
            elements.push(<h1 key={4}>Bestial failure</h1>);
            className = "danger";
        } else {
            // regular
            elements.push(<h1 key={4}>Failure</h1>);
            className = "failure";
        }

        marginContent = <span>a <strong>{Math.abs(successes - (difficulty || 0))}</strong>-degree margin of failure.</span>;
    }

    elements.push(<div key={5}>{successContent} {marginContent}</div>);

    let textCss = "text-white";
    if (className === "failure") {
        textCss = "text-failure"
    } else if (className === "win") {
        textCss = "text-win"
    } else if (className === "messy") {
        textCss = "text-messy";
    }

    const css = classNames([
        "card",
        `bg-${className}`,
        textCss,
        "mb-3",
    ]);

    return (
        <div className={css}>
            <div className="card-body">{elements}</div>
        </div>
    );
};
